import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { usePathHistoryContext } from '@context/PathHistoryContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useEntityConfig } from '@context/TradingAccountEntityConfigurationContext'

import { Body1 } from '@ui/Typography'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { isOne } from '@utils/validations'

import { LeverageTypeDto, isLeverageTypeDynamic } from '@model/LeverageTypeDto'
import { NameDto } from '@model/NameDto'
import {
  PlatformTypeEnum,
  isTMTPlatformType,
  isTradingViewPlatformType,
} from '@model/PlatformTypeEnum'
import { CurrencyType, WalletDto } from '@model/WalletDto'

import { Button } from '@global/button/Button'
import { CancelActionModal } from '@global/modal/CancleActionModal'
import { InfoModal } from '@global/modal/InfoModal'
import { Modal } from '@global/modal/Modal'

import { AccountTypeModal } from '../AccountTypeModal'
import { CampaignCurrencyModal } from '../CampaignCurrencyModal'
import { DynamicLeverageModal } from '../DynamicLeverageModal'
import { LeverageModal } from '../LeverageModal'
import { RegularCampaignAccountTypeModal } from '../RegularCampaignAccounTypeModal'
import { TradingAccountWalletModal } from '../TradingAccountWalletModal'
import { TradingPlatformModal } from '../TradingPlatformModal'
import {
  CreateTradingAccountCampaignFormProps,
  PlatformDataType,
  TradingAccountCreationOptionsFetchType,
} from './CreateTradingAccountCampaign.types'
import { CampaignExtendedNameDto } from './CreateTradingAccountCampaignForm'

import styles from '../TradingAccountsPage.module.scss'

type ModalsProps = CreateTradingAccountCampaignFormProps

type ModalsComponentProps = {
  onTradingPlatformSubmit(platform?: NameDto): void
  onSubmitForm(): Promise<void>
}

export const useModals = (
  props: ModalsProps,
  platformDataProps: PlatformDataType,
  tradingAccountCreationOptionsFetchProps: TradingAccountCreationOptionsFetchType
) => {
  const { values, currencies, hasCampaignQuery } = props
  const { setFieldValue, setTouched, setSelectedPlatform } = props
  const { accountTypes, platformsAllowToOpenAccount } = platformDataProps
  const { campaignCurrencies } = tradingAccountCreationOptionsFetchProps

  const { t } = useTranslation()
  const location = useLocation()
  const locale = useSessionLanguage()
  const entityConfig = useEntityConfig()
  const apiClient = useApiClient(ClientApiClient)

  const { selectedPlatformType } = location.state || {}

  const [isWalletModalOpen, setWalletModalOpen] = useState(false)
  const [isCurrencyModalOpen, setCurrencyModalOpen] = useState(false)
  const [isPlatformTypeModalOpen, setPlatformTypeModalOpen] = useState(false)
  const [isMarkupModalOpen, setMarkupModalOpen] = useState(false)
  const [isLeverageModalOpen, setLeverageModalOpen] = useState(false)
  const [isRegularCampaignAccountTypeModalOpen, setRegularCampaignAccountTypeModalOpen] =
    useState(false)
  const [isAccountTypeModalOpen, setAccountTypeModalOpen] = useState(false)
  const [isCancelModalOpen, setCancelModalOpen] = useState(false)
  const [isDynamicLeverageModalOpen, setDynamicLeverageModalOpen] = useState(false)

  const [tmpPlatformType, setTmpPlatformType] = useState<NameDto | undefined>(
    values.platformType || selectedPlatformType
  )

  const getDefaultName = (name: string): string => {
    if (name.startsWith('Classic')) {
      return `Classic (${t('Most Popular')})`
    }
    return name
  }

  const updatedAccountTypeName = useMemo(() => {
    return accountTypes.map((accountType) => ({
      ...accountType,
      name: getDefaultName(accountType.name),
    }))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountTypes, locale, entityConfig])

  const handleSetTmpPlatformType = (platform: NameDto) => {
    setTmpPlatformType(platform)
  }

  const handleSetLeverage = (leverage: LeverageTypeDto) => {
    setFieldValue('leverageType', leverage)
    setLeverageModalOpen(false)
  }

  const handleSetAccountType = (accountType: NameDto) => {
    setFieldValue('accountType', accountType)
    if (!isLeverageTypeDynamic(values.leverageType?.id) && !isOne(values.leverageTypes.length)) {
      setFieldValue('leverageType', undefined)
    }
    setAccountTypeModalOpen(false)
  }

  const handleSetAccountOrCampaign = (value: CampaignExtendedNameDto) => {
    handleCurrencyId()
    refreshLeverageType(undefined)
    location.state = {}
    setSelectedPlatform(undefined)
    setTmpPlatformType(undefined)

    if (value.id === '0') {
      setFieldValue('tradingAccount', value)
      setFieldValue('campaignId', null)
      setFieldValue('platformType', undefined)
      setFieldValue('wallet', undefined)
      setFieldValue('accountType', undefined)
      return setRegularCampaignAccountTypeModalOpen(false)
    }
    setTouched({ currencyId: false })
    setFieldValue('terms', false)
    setFieldValue('calculationType', value.calculationType)
    setFieldValue('platformType', undefined)
    setFieldValue('accountType', undefined)
    setFieldValue('campaignId', value.id)
    setFieldValue('tradingAccount', value)
    setRegularCampaignAccountTypeModalOpen(false)
  }

  const handleCurrencyId = () => {
    if (isOne(campaignCurrencies.length)) {
      setFieldValue('currencyId', campaignCurrencies[0])
    } else {
      setFieldValue('currencyId', undefined)
    }
  }

  const refreshLeverageType = async (platformId: PlatformTypeEnum | undefined) => {
    if (isTMTPlatformType(platformId) || isTradingViewPlatformType(platformId)) {
      const dynamicLeverage = (await apiClient.getLeverageTypes(platformId)).find((leverage) =>
        isLeverageTypeDynamic(leverage.id)
      )
      if (dynamicLeverage) {
        setFieldValue('leverageType', dynamicLeverage)
      } else {
        setFieldValue('leverageType', undefined)
      }
    } else {
      setFieldValue('leverageType', undefined)
    }
  }

  const handleWallet = (wallet: WalletDto) => {
    setFieldValue('wallet', wallet)
    setFieldValue('accountType', undefined)
    if (!isLeverageTypeDynamic(values.leverageType?.id)) {
      setFieldValue('leverageType', undefined)
    }
    setWalletModalOpen(false)
  }

  const handleCurrency = (currencyId: CurrencyType) => {
    setFieldValue('currencyId', currencyId)
    setCurrencyModalOpen(false)
  }

  return {
    values,
    setAccountTypeModalOpen,
    setMarkupModalOpen,
    setCurrencyModalOpen,
    setCancelModalOpen,
    setPlatformTypeModalOpen,
    setRegularCampaignAccountTypeModalOpen,
    setLeverageModalOpen,
    tmpPlatformType,
    setWalletModalOpen,
    setDynamicLeverageModalOpen,
    isWalletModalOpen,
    handleWallet,
    isDynamicLeverageModalOpen,
    isMarkupModalOpen,
    currencies,
    isCurrencyModalOpen,
    campaignCurrencies,
    handleCurrency,
    isPlatformTypeModalOpen,
    platformsAllowToOpenAccount,
    handleSetTmpPlatformType,
    isRegularCampaignAccountTypeModalOpen,
    hasCampaignQuery,
    handleSetAccountOrCampaign,
    isLeverageModalOpen,
    isAccountTypeModalOpen,
    isCancelModalOpen,
    handleSetLeverage,
    updatedAccountTypeName,
    handleSetAccountType,
  }
}

export const CreateTradingAccountCampaignModals = React.memo(
  (props: ModalsComponentProps & ModalsProps & ReturnType<typeof useModals>) => {
    const {
      data,
      values,
      setAccountTypeModalOpen,
      setMarkupModalOpen,
      setCurrencyModalOpen,
      setCancelModalOpen,
      setPlatformTypeModalOpen,
      setRegularCampaignAccountTypeModalOpen,
      setLeverageModalOpen,
      tmpPlatformType,
      setWalletModalOpen,
      setDynamicLeverageModalOpen,
      isWalletModalOpen,
      handleWallet,
      isDynamicLeverageModalOpen,
      isMarkupModalOpen,
      currencies,
      isCurrencyModalOpen,
      campaignCurrencies,
      handleCurrency,
      isPlatformTypeModalOpen,
      platformsAllowToOpenAccount,
      handleSetTmpPlatformType,
      isRegularCampaignAccountTypeModalOpen,
      hasCampaignQuery,
      handleSetAccountOrCampaign,
      isLeverageModalOpen,
      isAccountTypeModalOpen,
      isCancelModalOpen,
      handleSetLeverage,
      updatedAccountTypeName,
      handleSetAccountType,
    } = props

    const { t } = useTranslation()
    const isMobile = useWindowResize()
    const { navigateToPreviousPath } = usePathHistoryContext()
    const navigate = useNavigate()

    return (
      <>
        {isDynamicLeverageModalOpen && (
          <DynamicLeverageModal onClose={() => setDynamicLeverageModalOpen(false)} />
        )}
        {isMarkupModalOpen && (
          <Modal
            closeModal={() => setMarkupModalOpen(false)}
            render={({ closeModal }) => (
              <MarkupInfoModal onSubmit={props.onSubmitForm} onClose={closeModal} />
            )}
          />
        )}
        {isWalletModalOpen && (
          <TradingAccountWalletModal
            walletId={values.wallet?.id}
            currencies={currencies}
            onSelectOption={handleWallet}
            onClose={() => setWalletModalOpen(false)}
          />
        )}
        {isCurrencyModalOpen && (
          <CampaignCurrencyModal
            options={campaignCurrencies}
            selectedCurrencyId={values.currencyId}
            onSelectOption={handleCurrency}
            onClose={() => setCurrencyModalOpen(false)}
          />
        )}
        {!isMobile && isPlatformTypeModalOpen && (
          <TradingPlatformModal
            tradingAccountsAccountInfo={platformsAllowToOpenAccount}
            onSelectOption={handleSetTmpPlatformType}
            onClose={() => setPlatformTypeModalOpen(false)}
            onConfirm={props.onTradingPlatformSubmit}
            tmpPlatformType={tmpPlatformType}
          />
        )}
        {isRegularCampaignAccountTypeModalOpen && (
          <RegularCampaignAccountTypeModal
            campaigns={data}
            isCampaign={hasCampaignQuery}
            onSelectOption={handleSetAccountOrCampaign}
            onClose={() => setRegularCampaignAccountTypeModalOpen(false)}
          />
        )}
        {isLeverageModalOpen && (
          <LeverageModal
            title={t('Trading Account.Select Leverage')}
            platformTypeId={values.platformType?.id}
            currentLeverage={values?.leverageType}
            restrictions={[]}
            onSelect={handleSetLeverage}
            onClose={() => setLeverageModalOpen(false)}
          />
        )}
        {isAccountTypeModalOpen && (
          <AccountTypeModal
            accountTypes={updatedAccountTypeName}
            accountType={values.accountType}
            onSelect={handleSetAccountType}
            onClose={() => setAccountTypeModalOpen(false)}
          />
        )}
        {isCancelModalOpen && (
          <Modal
            closeModal={() => setCancelModalOpen(false)}
            render={() => (
              <CancelActionModal
                onConfirm={() => {
                  if (isMobile) {
                    navigate('/dashboard/traders-room/trading-accounts', { replace: true })
                  } else {
                    navigateToPreviousPath({
                      state: { fromAddCampaignTradingAccountForm: true, wallets: true },
                    })
                  }
                }}
                onCancel={() => setCancelModalOpen(false)}
              />
            )}
          />
        )}
      </>
    )
  }
)

interface ModalProps {
  onSubmit?(): void
  onClose(): void
}

const MarkupInfoModal: React.FC<ModalProps> = (props) => {
  const { onSubmit = () => {}, onClose } = props

  const { t } = useTranslation()

  return (
    <InfoModal
      title={t('Trading Account.Introducing Broker (IB)')}
      renderBody={() => (
        <section className='modal-card-body'>
          <Body1 isParagraph className={styles.text}>
            {t('Trading Account.Markup Modal')}
          </Body1>
        </section>
      )}
      renderFooter={() => (
        <Button appearance='ghost' onClick={onSubmit}>
          {t('Got It')}
        </Button>
      )}
      onConfirm={onSubmit}
      onCancel={onClose}
    />
  )
}
